import axios from "axios";
import React, { useState } from "react";
import styled from "styled-components";
import { Cost, displayCost, FirebaseFiles } from "sweet-global";
import CheckIcon from "sweet-web/components/icons/CheckIcon";
import { ItemLabel, ItemTitle } from "sweet-web/components/universal/Text";
import { colors } from "sweet-web/lib/styles";

/*
Option Input
Input that lets you select from multiple options
*/

const Wrapper = styled.div`
  padding: 10px 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  transition: all 150ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
`;

const OptionTitle = styled.h5`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.nearBlack};
  transition: all 150ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
`;

const Option = styled.div<{ checked: boolean }>`
  width: 100%;
  user-select: none;
  font-size: 14px;
  font-weight: 600;
  color: ${props => (props.checked ? colors.primary : colors.nearBlack)};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  flex-basis: 50%;
  &:hover {
    color: ${colors.primary};
    .sweet-option-input-option-title {
      color: ${colors.primary};
    }
    .sweet-option-input-cost-text {
      color: ${colors.primary};
    }
    span {
      border-color: ${colors.primary};
    }
  }
  span {
    border-color: ${props => (props.checked ? colors.primary : colors.gray)};
  }
  .sweet-option-input-check-icon {
    opacity: ${props => (props.checked ? 1 : 0)};
    transition: all 100ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  }
  .sweet-option-input-option-title {
    color: ${props => (props.checked ? colors.primary : colors.nearBlack)};
  }
  .sweet-option-input-cost-text {
    color: ${props => (props.checked ? colors.primary : colors.gray)};
  }
`;

const LeftOption = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const OptionCosts = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
`;

const Content = styled.div`
  padding: 5px 10px;
  border-radius: 5px;
  border: 1px solid ${colors.lightGray};
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  transition: all 100ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
`;

const CostText = styled.p`
  font-size: 12px;
  color: ${colors.gray};
  font-weight: 500;
  text-align: right;
  transition: all 100ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
`;

const Checkbox = styled.span`
  display: flex;
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 1px solid ${colors.gray};
  margin-right: 10px;
  justify-content: center;
  align-items: center;
  transition: all 100ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
`;

type Props = {
  title: string;
  options: Array<{
    id: string;
    title: string;
    cost?: Cost;
  }>;
  value: {
    [optionId: string]: boolean;
  };
  onChange: (value: { [optionId: string]: boolean }) => void;
};
const OptionInput: React.FC<Props> = props => {
  return (
    <Wrapper>
      {props.title && <ItemTitle>{props.title}</ItemTitle>}
      <Content>
        {props.options.map(option => (
          <Option
            checked={props.value[option.id] === true}
            key={`option-input-option-${option.id}`}
            onClick={() => {
              const newValue = {
                ...props.value,
                [option.id]: !props.value[option.id]
              };
              props.onChange(newValue);
            }}
          >
            <LeftOption>
              <Checkbox>
                <CheckIcon
                  height={10}
                  color={colors.primary}
                  className="sweet-option-input-check-icon"
                />
              </Checkbox>
              <OptionText>
                <OptionTitle className="sweet-option-input-option-title">
                  {option.title}
                </OptionTitle>
              </OptionText>
            </LeftOption>

            {option.cost && (
              <CostText
                key={`option-input-option-${option.id}-cost`}
                className="sweet-option-input-cost-text"
              >
                From {displayCost(option.cost)}
              </CostText>
            )}
          </Option>
        ))}
      </Content>
    </Wrapper>
  );
};

export default OptionInput;
